import {
  createQColumnHelper,
  DataProvider,
  Filtering,
  Pagination,
  QBodyLayout,
  QDataTable,
  QEmptyState,
  QHeading,
  QStack,
  Sorting,
  useCurrentUser,
  useFeatureFlag,
} from '@qualio/ui-components';

import { useFlags } from 'launchdarkly-react-client-sdk';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { documentApi } from '../../../api/document';
import {
  DocumentStatus,
  EnrichedQualioDocument,
  QualioDocument,
} from '../../../api/model/document';
import { Tag } from '../../../api/model/tag';
import { TagApi } from '../../../api/tag';
import { Template } from '../../../api/templateApi';
import { useDocumentTitle } from '../../../hooks/useDocumentTitle';
import { useTemplates } from '../../../hooks/useTemplates';
import { OverviewRoutes } from '../DocumentOverview/routes';
import {
  ActiveLibraryTrainingFilter,
  ActiveTagFilter,
  ActiveTemplateFilter,
  TemplatesFilter,
} from '../DocumentWorkspace/WorkspaceFilters';
import { ExportButton } from './ExportButton';
import { SearchFilter } from './SearchFilter';
import { TagsFilter } from './TagsFilter';
import { FOR_TRAINING_FILTER, TrainingFilter } from './TrainingFilter';
import { appendDocumentIconProperties } from '../../../hooks/useDocumentTableColumns';
const columnHelper = createQColumnHelper<EnrichedQualioDocument>();

export const DEFAULT_PAGE_SIZE = 15;

export const DocumentLibrary: React.FC = () => {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const { documentsTaskDrawer } = useFlags();
  const featureFlags = useFeatureFlag();
  const enterpriseAccounts = Boolean(featureFlags['enterprise-accounts']);
  const {
    Provider: PaginationProvider,
    offset,
    limit,
  } = Pagination.useLimitOffset();

  useDocumentTitle('Library');

  const fetchDocuments = async ({
    pageParam,
  }: {
    pageParam: DataProvider.PageParams;
  }): Promise<DataProvider.TDataWithCount<QualioDocument>> => {
    const params = pageParam?.toUrlSearchParams();
    const tagsParam = params.get('filteringTag');
    const tagsIds = tagsParam ? tagsParam.split(',') : [];
    const templatesParam = params.get('filteringTemplates');
    const templatesCodes = templatesParam ? templatesParam.split(',') : [];
    const orderBy = params.get('order_by');
    const searchQuery = params.get('query');
    const filterBy = params.get('filter_by');
    const fetchMethod =
      filterBy === FOR_TRAINING_FILTER
        ? documentApi.fetchUserTrainingDocs
        : documentApi.fetchAll;
    const results = await fetchMethod(
      currentUser,
      [DocumentStatus.Effective],
      searchQuery,
      tagsIds,
      offset,
      limit,
      orderBy,
      templatesCodes,
    );

    return {
      itemCount: results.total,
      data: results.documents,
    };
  };

  const loadTags = async (): Promise<Tag[]> => TagApi.fetchTags(currentUser);

  const { data: fetchedTags = [], isLoading: isLoadingTags } = useQuery({
    queryFn: loadTags,
    queryKey: ['tags', currentUser.companyId],
  });

  const enrichDocument = useCallback(
    (document: QualioDocument) => ({
      ...document,
      tags: fetchedTags.filter((tag) => document.tag_ids.includes(tag.id)),
    }),
    [fetchedTags],
  );

  const { data: templates = [], isLoading: isLoadingTemplates } = useTemplates({
    includeArchived: true,
  });

  const tagsMap = useMemo(() => {
    return fetchedTags.reduce(
      (acc, tag) => {
        acc[tag.id] = tag;
        return acc;
      },
      {} as Record<string, Tag>,
    );
  }, [fetchedTags]);

  const templatesMap = useMemo(() => {
    return templates.reduce(
      (acc, template) => {
        acc[template.prefix] = template;
        return acc;
      },
      {} as Record<string, Template>,
    );
  }, [templates]);

  const activeTagFilterRender = useCallback(
    (value: unknown) => <ActiveTagFilter value={value} tagsMap={tagsMap} />,
    [tagsMap],
  );

  const activeTemplateFilterRender = useCallback(
    (value: unknown) => (
      <ActiveTemplateFilter value={value} templatesMap={templatesMap} />
    ),
    [templatesMap],
  );

  const activeLibraryTrainingFilterRender = useCallback(
    (value: unknown) => <ActiveLibraryTrainingFilter value={value} />,
    [],
  );

  const filterDefinitions = useMemo(
    () => ({
      filteringTag: {
        label: t('labels.tag'),
        schema: Filtering.schemas.StringSchema().array().nullish(),
        activeRender: activeTagFilterRender,
      },
      filteringTemplates: {
        label: t('labels.type'),
        schema: Filtering.schemas.StringSchema().array().nullish(),
        activeRender: activeTemplateFilterRender,
      },
      filter_by: {
        label: t('labels.filterBy'),
        schema: Filtering.schemas.StringSchema().nullish(),
        activeRender: activeLibraryTrainingFilterRender,
      },
    }),
    [
      activeTagFilterRender,
      activeTemplateFilterRender,
      activeLibraryTrainingFilterRender,
      t,
    ],
  );

  const columns = useMemo(() => {
    return [
      columnHelper.id((document) => document.code, {
        header: t('tableHeaders.id'),
        id: 'code',
        width: '100px',
      }),
      columnHelper.textLink(
        'title',
        (qualioDoc) =>
          documentsTaskDrawer
            ? `/library/documents/${qualioDoc.id}/${OverviewRoutes.CONTENT}`
            : `/library/documents/${qualioDoc.id}`,
        {
          header: t('tableHeaders.title'),
          id: 'title',
          width: '382px',
          ...(enterpriseAccounts ? appendDocumentIconProperties : {}),
        },
      ),
      columnHelper.text((document) => document.type, {
        header: t('tableHeaders.type'),
        id: 'type',
        width: '250px',
      }),
      columnHelper.tag(
        (document) =>
          document.tags.map((tag) =>
            tag.group_ids.length
              ? { label: tag.name, leftIcon: 'Lock' as const }
              : tag.name,
          ),
        {
          statuses: { '*': 'default' },
          header: t('tableHeaders.tags'),
          enableSorting: false,
          width: '300px',
        },
      ),
      columnHelper.text(
        (document) => `${document.major_version}.${document.minor_version}`,
        {
          header: t('tableHeaders.version'),
          id: 'version',
          width: '120px',
        },
      ),
    ];
  }, [documentsTaskDrawer, enterpriseAccounts, t]);

  return (
    <div style={{ width: '100%' }}>
      <Filtering.FilterProvider
        searchTermKey="query"
        definitions={filterDefinitions}
        persistanceKey={null} // this can only be used once we fixed manual handling for query params and filters
      >
        <QBodyLayout.Default>
          <PaginationProvider>
            <Sorting.DefaultSortingProvider sortByKey="order_by">
              <DataProvider.Remote
                queryFn={fetchDocuments}
                queryKey={['getDocuments', currentUser.userId]}
                queryParamKeys={{
                  searchTerm: 'query',
                  sortBy: 'order_by',
                  pageSize: 'limit',
                  pageIndex: 'pageIndex',
                }}
                enricher={enrichDocument}
              >
                <QStack isInline justify="space-between">
                  <QHeading size="lg" mb={6} data-cy="library-heading">
                    {t('labels.library')}
                  </QHeading>
                  <ExportButton />
                </QStack>
                <QStack isInline>
                  <TemplatesFilter
                    templates={templates}
                    isLoadingTemplates={isLoadingTemplates}
                  />
                  <TagsFilter
                    isLoadingTags={isLoadingTags}
                    fetchedTags={fetchedTags}
                  />
                  <TrainingFilter />
                  <SearchFilter id="library" />
                </QStack>
                <QDataTable columns={columns}>
                  <QEmptyState
                    title={t('emptyStates.noDocumentsFound')}
                    subtitle={t(
                      'emptyStates.thereAreNoApprovedOrEffectiveDocuments',
                    )}
                    data-cy="library-table-empty-state"
                  />
                </QDataTable>
              </DataProvider.Remote>
            </Sorting.DefaultSortingProvider>
          </PaginationProvider>
          <></>
        </QBodyLayout.Default>
      </Filtering.FilterProvider>
    </div>
  );
};
